import { CellContext } from '@tanstack/react-table';
import { getTableMetaHelper, Skeleton } from '@round/ui-kit';
import AllocationBadge from 'Modules/Plans/Finance/PurchaseOrders/components/AllocationBadge/AllocationBadge';
import { CampaignTableRow } from 'Modules/Plans/Project/ProjectDetails/components/ProjectCampaignsTable/ProjectCampaignsTable';
import { Button, assets } from '@round/whitelabel-ui';
import styles from './AllocationCell.module.css';
import FinanceStatusSelect from './FinanceStatusSelect';
import { creatorbase } from '@round/api';
import { showNotification } from 'helpers';
import { useState } from 'react';
import PurchaseOrderBadges from 'Modules/Plans/Finance/PurchaseOrders/components/PurchaseOrderBadges/PurchaseOrderBadges';

const { Pencil: PencilIcon } = assets;

type Row = CampaignTableRow;
type Context<TRow extends Row> = CellContext<TRow, TRow[keyof TRow]>;
export type Meta<TRow extends Row> = {
    isLoading?: boolean;
    onAllocate?: (row: TRow) => void;
    updateCampaign?: typeof creatorbase.patchCampaign;
    arePurchaseOrdersLoading?: boolean;
};

const AllocationCell = <TRow extends Row, TMeta extends Meta<TRow>>({ row, table }: Context<TRow>) => {
    const getTableMeta = getTableMetaHelper<TMeta>();
    const { isLoading, onAllocate, updateCampaign, arePurchaseOrdersLoading } = getTableMeta(table);
    const [isUpdateLoading, setIsUpdateLoading] = useState(false);

    if (isLoading) {
        return <Skeleton />;
    }

    if (typeof row.original.run_by_partnership_id === 'number') {
        return '-';
    }

    const cost = row.original.cost ?? 0;
    const isFullyAllocated = cost > 0 && cost - row.original.purchase_orders_allocated_amount === 0;
    const purchaseOrders = row.original.purchaseOrders ?? [];

    return (
        <div className={styles.container}>
            <div className={styles.row}>
                {isFullyAllocated ? (
                    <AllocationBadge
                        originalAmount={row.original.cost ?? 0}
                        allocatedAmount={row.original.purchase_orders_allocated_amount}
                    />
                ) : (
                    <FinanceStatusSelect
                        isUpdateLoading={isUpdateLoading}
                        isDisabled={row.original.finance_status === 'invoiced'}
                        value={row.original.finance_status}
                        onChange={(value) => {
                            if (value !== row.original.finance_status) {
                                setIsUpdateLoading(true);
                                updateCampaign?.(row.original.id, { finance_status: value })
                                    ?.then((res) => {
                                        if (res.status === 200) {
                                            showNotification('Status updated', 'info');
                                            return;
                                        }

                                        showNotification('Could not update status', 'error');
                                    })
                                    .catch(() => {
                                        showNotification('Could not update status', 'error');
                                    })
                                    .finally(() => setIsUpdateLoading(false));
                            }
                        }}
                    />
                )}

                <Button
                    className={styles.allocateButton}
                    appearance="ghost"
                    onClick={(e) => {
                        e.stopPropagation();
                        onAllocate?.(row.original);
                    }}
                >
                    <PencilIcon />
                </Button>
            </div>

            <PurchaseOrderBadges isLoading={arePurchaseOrdersLoading} purchaseOrders={purchaseOrders} />
        </div>
    );
};

export default AllocationCell;
